import { useMemo } from "react";
import { MAX_DRIVE_HOURS_PER_DAY } from "@/lib/pricing";

// ETA math shared by the schedule, quote PDFs and driver route sheet:
// - deliveryEstimate: overall one-way time chunked into 10h driving days
// - stopEtas: cumulative per-drop arrival dates
// - stopSplits: per-customer price share proportional to leg miles
export function useEtas({ routeData, stops, numStops, stopTimeMin, pickupDate, recommendation, customers }) {
  const deliveryEstimate = useMemo(() => {
    if (!routeData?.legs?.length) return null;
    const oneWayMinutes =
      routeData.legs.length > 1
        ? routeData.legs.slice(0, -1).reduce((a, l) => a + (l.minutes || 0), 0)
        : routeData.total_minutes / 2;
    const hours = oneWayMinutes / 60 + (numStops * (Number(stopTimeMin) || 0)) / 60;
    const days = Math.max(1, Math.ceil(hours / MAX_DRIVE_HOURS_PER_DAY));
    const assumed = !pickupDate;
    const base = pickupDate || new Date().toISOString().slice(0, 10);
    const d = new Date(`${base}T12:00:00`);
    d.setDate(d.getDate() + (days - 1));
    return { hours: Math.round(hours * 10) / 10, days, date: d.toISOString().slice(0, 10), assumed };
  }, [routeData, numStops, stopTimeMin, pickupDate]);

  const stopEtas = useMemo(() => {
    if (!routeData?.legs?.length) return [];
    const validStops = stops.filter((s) => s.value.trim());
    const dropLegs = routeData.legs.length > 1 ? routeData.legs.slice(0, -1) : routeData.legs;
    const assumed = !pickupDate;
    const base = pickupDate || new Date().toISOString().slice(0, 10);
    let cumMin = 0;
    return validStops.map((s, i) => {
      cumMin += (dropLegs[i]?.minutes || 0) + (i > 0 ? Number(stopTimeMin) || 0 : 0);
      const hours = cumMin / 60;
      const days = Math.max(1, Math.ceil(hours / MAX_DRIVE_HOURS_PER_DAY));
      const d = new Date(`${base}T12:00:00`);
      d.setDate(d.getDate() + (days - 1));
      return {
        address: s.value,
        customer: s.customer || "",
        hours: Math.round(hours * 10) / 10,
        days,
        date: d.toISOString().slice(0, 10),
        assumed,
      };
    });
  }, [stops, routeData, stopTimeMin, pickupDate]);

  const stopSplits = useMemo(() => {
    const validStops = stops.filter((s) => s.value.trim());
    const dropLegs = routeData?.legs?.length > 1 ? routeData.legs.slice(0, -1) : null;
    if (validStops.length < 2 || !recommendation || !dropLegs) return [];
    const weights = validStops.map((_, i) => (dropLegs[i] ? dropLegs[i].miles : 1));
    const totalW = weights.reduce((a, b) => a + b, 0) || 1;
    const price = recommendation.price;
    return validStops.map((s, i) => ({
      address: s.value,
      customer: s.customer || customers[i]?.name || "",
      pct: Math.round((weights[i] / totalW) * 100),
      share: Math.round((price * weights[i]) / totalW * 100) / 100,
    }));
  }, [stops, routeData, recommendation, customers]);

  return { deliveryEstimate, stopEtas, stopSplits };
}
